<template>
  <v-app>
    <!-- left navigation panel when on smartphone -->
    <v-navigation-drawer
        temporary
        v-model="drawer"
        width="320"
      >
      <v-divider></v-divider>
      <v-list
        density="compact"
        class="pt-0"
      >
        <v-list-item
          v-for="(item, index) in menuItems.filter(item => item.link)"
          :key="item.title + index"
          :title="item.title"
          router
          :to="item.link"
          :prepend-icon="item.icon"
          :icon="item.icon"
        >
        </v-list-item>

        <v-list-group
          v-for="(item, index) in menuItems.filter(item => !item.link)"
          :key="'parent' + index"
          v-show="isAuthenticated"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              class="bg-grey-darken-3"
              :title="item.title"
              :prepend-icon="item.icon"
            >
            </v-list-item>
          </template>

          <v-list-item
          v-for="childItem in item.children"
          :key="item.title + childItem.title"
          :value="item.title + childItem.title"
          :prepend-icon="childItem.icon"
          :title="childItem.title"
          router
          :to="childItem.link === '/tickets'
            ? { path: childItem.link, query: { participantSk: currentParticipantSk } }
            : childItem.link"
          >
          </v-list-item>

        </v-list-group>
        <v-list-item
          v-if="!isAuthenticated"
          flat
          prepend-icon="mdi-lock-open"
          @click="login"
        >
          <v-list-item-title>Log in</v-list-item-title>
        </v-list-item>
      </v-list>
      <template v-slot:append>
        <v-list class="text-center">
          <v-list-item
            v-if="isAuthenticated"
            @click="logout"
            class="mb-auto"
            title="Log out"
            prepend-icon="mdi-logout"
          >
            Log out
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- top bar when on desktop -->
    <v-app-bar density="compact">
      <v-app-bar-title>
      <v-app-bar-nav-icon @click="drawer = !drawer" class="hidden-lg-and-up"></v-app-bar-nav-icon>
        <router-link to="/" v-slot="{ navigate }" custom>
          <span @click="navigate" style="cursor: pointer">
            Generation Research - Panel <span class="hidden-log-and-down">Studio</span>
          </span>
        </router-link>
        <span v-if="isAuthenticated && selectionConfig.isLocked" class="ml-2">
          <span v-if="selectionConfig.rowId === 2 || selectionConfig.rowId === 3" class="text-orange">
            {{ currentParticipantName }}
          </span>
          <span v-else-if="selectionConfig.rowId === 4" class="text-orange">
            {{ currentBrandName }}
          </span>
          <span v-else-if="selectionConfig.rowId === 5" class="text-orange">
            <v-chip size="x-small" color="warning" variant="outlined" class="mr-1">No Brand</v-chip>
            {{ currentParticipantName }}
          </span>
        </span>
        <span v-else-if="isAuthenticated && allParticipants?.length && !$apollo.queries.allParticipants?.loading">
          <span class="text-grey ml-2">Nothing loaded</span>
        </span>
      </v-app-bar-title>
      <div
        class="text-white"
        style="font-size: 9px;"
        v-if="mode !== 'production'"
      >
        Auth0 Tenant: '{{ tenant }}'<br>
        <br>
      </div>
      <v-spacer></v-spacer>

      <v-toolbar-items class="hidden-xs">

        <v-list-item
          v-for="item in menuItems.filter(item => item.link)"
          :key="item.title"
        >
          <!-- TODO: Hide step03 menu if not applicable -->
          <v-btn
            v-show="isAuthenticated && $route.path !== '/'"
            variant="text"
            router
            :to="item.link"
            :prepend-icon="item.icon"
            :key="item.title"
          >
            {{item.title}}
          </v-btn>
        </v-list-item>
        <v-menu
          v-for="(item, index) in menuItems.filter(item => !item.link)"
          :key="'parent' + index"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              v-show="isAuthenticated"
              :class="{ 'v-btn--active': item.children.map(x => x.link).includes($route.path) }"
              :prepend-icon="item.icon"
            >
              {{item.title}}
            </v-btn>
          </template>
          <v-list
            density="compact"
            v-show="isAuthenticated"
          >
            <v-list-item
              v-for="childItem in item.children"
              :key="item.title + childItem.title"
            >
            <v-btn
              variant="text"
              router
              :to="childItem.link === '/tickets'
                ? { path: childItem.link, query: { participantSk: currentParticipantSk } }
                : childItem.link"
                :prepend-icon="childItem.icon"
              >
                {{childItem.title}}
            </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-list-item
          v-if="isAuthenticated"
          class="px-1"
          size="small"
          plain
        >
          <span class="text-grey-lighten-2" style="font-size: 12px;">{{ loggedInUser?.name }}</span>
        </v-list-item>
        <v-btn
          v-if="isAuthenticated"
          variant="text"
          title="Logout"
          icon="mdi-logout"
          @click="logout"
        >
        </v-btn>

        <v-btn
          v-if="!isAuthenticated"
          variant="text"
          @click="login"
          prepend-icon="mdi-lock-open"
        >
          Log in
        </v-btn>
      </v-toolbar-items>
    </v-app-bar>
    <v-main>
      <v-row v-if="errors.length">
        <v-col cols="12">
          <div>
            <v-alert :value="!!errors.length" type="error" closable @update:model-value="resetErrors()">
                <span v-for="(value, errorIndex) in errors" :key="errorIndex">
                    {{ value }}<br/>
                </span>
            </v-alert>
          </div>
        </v-col>
      </v-row>

      <v-snackbar
        v-model="successSnackbar"
        color="success"
        timeout="4000"
        location="bottom"
      >
        {{ success }}
      </v-snackbar>

      <!-- Prevents watchers stacking up on TicketPage -->
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </v-main>
  </v-app>
</template>

<script>
import ALL_PRODUCT_MATCHINGS from '@/graphql/Product/allProductMatchings.gql'
import ALL_PRODUCTS from '@/graphql/Product/allProducts.gql'
import ALL_BRANDS from '@/graphql/Brand/allBrands.gql'
import ALL_LINES from '@/graphql/Line/allLines.gql'
import ALL_CATEGORIES from '@/graphql/Category/allCategories.gql'
import ALL_BENEFITS from '@/graphql/Benefit/allBenefits.gql'
import ALL_MULTI_PACKS from '@/graphql/MultiPack/allMultiPacks.gql'
import ALL_PARTICIPANTS from '@/graphql/Participant/allParticipants.gql'
import { mapState, mapMutations } from 'vuex'
export default {
  provide() {
    // Provide root component, accessible by child components via 'inject'
    // Not ideal but works
    return { rc: this }
  },
  data () {
    return {
      mode: '',
      tenant: '',
      drawer: false,
      menuItems: [
        { icon: 'mdi-account-group', title: 'Participants', link: '/' },
        {
          icon: 'mdi-auto-fix',
          title: 'Enrichment',
          children: [
            { icon: 'mdi-link-variant', title: 'Product Matching', link: '/product-matching' },
            { icon: 'mdi-history', title: 'History', link: '/history' },
          ]
        },
        // {
        //   icon: 'mdi-chart-box-outline',
        //   title: 'Facts',
        //   children: [
        //     { icon: 'mdi-order-bool-ascending-variant', title: 'Table view', link: '/facts-table' },
        //     { icon: 'mdi-camera-outline', title: 'Graph view', link: '/facts-graph' },
        //   ]
        // },
        {
          icon: 'mdi-database',
          title: 'Master',
          children: [
            { icon: 'mdi-shape', title: 'Categories', link: '/category-master-data' },
            { icon: 'mdi-gift-outline', title: 'Benefits / Multi-Pack', link: '/benefit-multipack-master-data' },
            { icon: 'mdi-tag', title: 'Brands / Houses / Companies', link: '/brand-master-data' },
            { icon: 'mdi-format-list-text', title: 'Lines', link: '/line-master-data' },
            { icon: 'mdi-cube-outline', title: 'Products', link: '/product-master-data' },
          ]
        },
        {
          icon: 'mdi-cog',
          title: 'Admin',
          children: [
            { icon: 'mdi-clipboard-text-clock', title: 'Mutation Logs', link: '/admin-mutation-logs' },
          ]
        }
      ],
      alert: false,
      allParticipants: [],
      allProductMatchings: [],
      allProducts: []
    }
  },
  watch: {
    allProductMatchingGroups () {
      if (this.hasMoreProductMatchingGroups) {
        this.loadMoreProductMatchingGroups()
      }
    },
    '$route.params.ticketSk' (newVal) {
      !newVal && this.resetCurrentTicketSk()
    },
    'selectionConfig.isLocked' (newVal) {
      // When selection is locked, refetch queries with correct variables
      if (newVal) {
        this.$nextTick(() => {
          if (this.selectionConfig.rowId !== 1) {
            this.$apollo.queries.allProductMatchings?.refetch()
          }
          this.$apollo.queries.allProducts?.refetch()
          this.$apollo.queries.allLines?.refetch()
        })
      }
    },
    $route(to) {
      if (!this.$auth0.isAuthenticated.value || !to || !this.mode !== 'prod') return
      // User access token updated within last 8 seconds, assume a Login event
      // TODO: Update this with the exact path that the user is redirected to after login
      if ((to.fullPath == '/' || to.fullPath?.includes('/?')) && (Date.now() - new Date(this.$auth0.user.updated_at) < 8000)) {
        this.addUserLog('Login', to.fullPath)
      } else {
        this.addUserLog('View', to.fullPath)
      }
    }
  },
  methods: {
    ...mapMutations(['resetErrors', 'resetSuccess']),
    login() {
      this.$auth0.loginWithRedirect()
    },
    logout() {
      this.$auth0.logout({
        logoutParams: {
          // eslint-disable-next-line compat/compat
          returnTo: window.location.origin
        }
      })
    },
    handleCopy(e) {
      // Get the selected text
      const selection = window.getSelection()
      const selectedText = selection.toString()

      // Trim leading and trailing whitespace (including tabs)
      const cleanedText = selectedText.trim()

      // Only intercept if there's actually whitespace to remove
      if (cleanedText !== selectedText && cleanedText.length > 0) {
        // Prevent default copy behavior
        e.preventDefault()
        // Copy cleaned text to clipboard
        e.clipboardData.setData('text/plain', cleanedText)
      }
    }
  },
  mounted() {
    // Add global copy event listener to clean clipboard data
    document.addEventListener('copy', this.handleCopy)
  },
  beforeUnmount() {
    // Remove global copy event listener
    document.removeEventListener('copy', this.handleCopy)
  },
  computed: {
    ...mapState(['errors', 'currentParticipantSk', 'success', 'selectionConfig']),
    loggedInUser() {
      return this.$auth0.user?.value
    },
    currentParticipantName () {
      const sk = this.selectionConfig?.participantSk
      return (this.allParticipants && sk)
        ? this.allParticipants?.find(x => x.Participant_SK === sk)?.Participant_Name
        : ''
    },
    currentBrandName () {
      const sks = this.selectionConfig?.brandSks || []
      if (!this.allBrands || sks.length === 0) return ''
      const names = sks.map(sk => this.allBrands.find(x => x.Brand_SK === sk)?.Brand_Name).filter(n => n)
      return names.join(', ')
    },
    isAuthenticated () {
      return this.$auth0.isAuthenticated.value
    },
    successSnackbar: {
      get() {
        return !!this.success
      },
      set(value) {
        if (!value) {
          this.resetSuccess()
        }
      }
    }
  },
  apollo: {
    allParticipants: {
      query: ALL_PARTICIPANTS,
      skip () {
        return !this.isAuthenticated
      }
    },
    // Queries based on selectionConfig - only fire after selection is locked
    allProductMatchings: {
      query: ALL_PRODUCT_MATCHINGS,
      variables () {
        const config = this.selectionConfig
        if (config.rowId === 2 || config.rowId === 3) {
          return { Participant_SK: config.participantSk }
        }
        if (config.rowId === 4) {
          // Multi-brand selection
          const brandSks = config.brandSks || []
          if (brandSks.length === 1) {
            return { Brand_FK: brandSks[0] }
          } else if (brandSks.length > 1) {
            return { Brand_FKs: brandSks }
          }
        }
        if (config.rowId === 5) {
          // No Brand + Participant
          return { Participant_SK: config.participantSk, noBrand: true }
        }
        return {}
      },
      skip () {
        const config = this.selectionConfig
        return !this.isAuthenticated ||
               !config.isLocked ||
               config.rowId === 1  // Row 1 = None, skip query
      }
    },
    allProducts: {
      query: ALL_PRODUCTS,
      variables () {
        const config = this.selectionConfig
        if (config.rowId === 3) {
          return { Participant_SK: config.participantSk }
        }
        if (config.rowId === 4) {
          // Multi-brand selection
          const brandSks = config.brandSks || []
          if (brandSks.length === 1) {
            return { Brand_FK: brandSks[0] }
          } else if (brandSks.length > 1) {
            return { Brand_FKs: brandSks }
          }
        }
        if (config.rowId === 5) {
          // For No Brand selection, show all products for the participant
          return { Participant_SK: config.participantSk }
        }
        return {}
      },
      skip () {
        const config = this.selectionConfig
        return !this.isAuthenticated ||
               !this.allParticipants.length ||
               !config.isLocked
      }
    },
    allLines: {
      query: ALL_LINES,
      variables () {
        const config = this.selectionConfig
        if (config.rowId === 3) {
          return { Participant_FK: config.participantSk }
        }
        if (config.rowId === 4) {
          // Multi-brand selection
          const brandSks = config.brandSks || []
          if (brandSks.length === 1) {
            return { Brand_FK: brandSks[0] }
          } else if (brandSks.length > 1) {
            return { Brand_FKs: brandSks }
          }
        }
        if (config.rowId === 5) {
          // For No Brand selection, show all lines for the participant
          return { Participant_FK: config.participantSk }
        }
        return {}
      },
      skip () {
        const config = this.selectionConfig
        return !this.isAuthenticated ||
               !this.allParticipants.length ||
               !config.isLocked
      }
    },
    allCategories: {
      query: ALL_CATEGORIES,
      skip () {
        // load only after participants are loaded, id after the cold start
        return !this.isAuthenticated || !this.allParticipants.length
      }
    },
    allBrands: {
      query: ALL_BRANDS,
      skip () {
        // load only after participants are loaded, id after the cold start
        return !this.isAuthenticated || !this.allParticipants.length
      }
    },
    allBenefits: {
      query: ALL_BENEFITS,
      skip () {
        // load only after participants are loaded, id after the cold start
        return !this.isAuthenticated || !this.allParticipants.length
      }
    },
    allMultiPacks: {
      query: ALL_MULTI_PACKS,
      skip () {
        // load only after participants are loaded, id after the cold start
        return !this.isAuthenticated || !this.allParticipants.length
      }
    }
  },
  name: 'App'
}
</script>
<style>
.v-data-table--dense td {
  padding-left: 2px !important;
  padding-right: 2px !important;
}
.v-data-table--dense th {
  padding-left: 2px !important;
  padding-right: 2px !important;
}

.v-list-item__action {
 margin-right: 8px !important;
}

.v-data-footer__select .v-input {
  margin-top: 4px !important;
  margin-bottom: 4px !important;
}

</style>
